import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { TableHead, Table, TableBody, TableCell, TableContainer, TableRow, TableFooter, TablePagination, Card, Box, Container, TableSortLabel, Stack, Button } from "@mui/material";
import Api from "../../network/Api";
import TypingIndicator from "../Shared/TypingIndicator";
import { ItemButtonActions } from "../Lessons/Buttons";
import { StartProps } from "../Layouts/MainApp";
import dayjs from "dayjs";
import SolutionDashboard from "./Dashboard";
import Progress from "./Progress";
import Toolbox from "../Shared/Toolbox";
import LessonSelection from "./LessonSelection";
import { Solution } from "../../types/Solutions";
import { DashboardItems } from "./Dashboard";

const SolutionsList = (props: StartProps & ItemButtonActions) => {

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<DashboardItems>();
  const [solutions, setSolutions] = useState<Array<Solution>>();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>("target_at");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const scrollTopRef = useRef();
  const [selectLessons, shouldSelectLessons] = useState<boolean>(pathname === '/solutions/new');

  useEffect(() => {
    Api.get(`/solutions/dashboard`).then(setDashboard);
  }, []);

  useEffect(() => {
    Api.get(`/solutions?limit=${limit}&page=${page}&order_by=${orderBy}&order=${direction}`).then(result => {
      setSolutions(result.items);
      setTotal(result.total);
      scrollTopRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [limit, page, direction, orderBy]);

  const SortLabel = ({ label, field }: { label: string, field: string }) => (
      <TableSortLabel
        active={orderBy === field}
        direction={direction}
        onClick={() => {
          if (orderBy === field) {
            setDirection(direction === "asc" ? "desc" : "asc");
          } else {
            setOrderBy(field);
          }
        }}
        sx={{color: "inherit !important"}}
      >
        {label}
      </TableSortLabel>
    );

  return <Box sx={{overflowY: 'auto', flexGrow: 1, width: "100%", pt: 3, pb: 5}}>
    <Container maxWidth="xl">

      <Stack spacing={3}>

        <SolutionDashboard items={dashboard} />

        <Card sx={{p: "0 !important"}}>
          <TableContainer sx={{width: '100%', overflowX: 'auto'}}>
            <Table>
              <TableHead ref={scrollTopRef}>
                <TableRow>
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 1 }}>
                    <SortLabel label="Issue" field="title" />

                    <Button component={NavLink} to="/solutions/new" variant="contained" color="secondary" size="small" onClick={() => shouldSelectLessons(true)}>Initiate new solution</Button>
                    { selectLessons && <Toolbox title="Select lessons learned" onClose={() => { shouldSelectLessons(false); navigate('/solutions'); }}>
                      <LessonSelection />
                    </Toolbox> }
                  </TableCell>
                  <TableCell><SortLabel label="Mitigation" field="mitigation" /></TableCell>
                  <TableCell align="center"><SortLabel label="Progress" field="state" /></TableCell>
                  <TableCell><SortLabel label="Target date" field="target_at" /></TableCell>
                  <TableCell><SortLabel label="Resolved at" field="resolved_at" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {solutions ? solutions.map((solution) => {
                  return <TableRow
                    key={solution.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, right: 0 }}>
                      <NavLink to={`/solutions/${solution.id}`}>{solution.title}</NavLink>
                    </TableCell>
                    <TableCell><NavLink to={`/solutions/${solution.id}`}>{solution.mitigation}</NavLink></TableCell>
                    <TableCell align="center" width="10%"><Progress state={solution.state} /></TableCell>
                    <TableCell><small className="text-secondary">{solution.target_at ? dayjs.unix(solution.target_at).format("lll") : "-"}</small></TableCell>
                    <TableCell><small className="text-secondary">{solution.resolved_at ? dayjs.unix(solution.resolved_at).format("lll") : "-"}</small></TableCell>
                  </TableRow>
                }) : <TableRow>
                  <TableCell colSpan={5} sx={{justifyItems: "center", padding: 5}}>
                    <TypingIndicator />
                  </TableCell>
                </TableRow>}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TablePagination 
                    count={total} 
                    page={page-1}
                    rowsPerPage={limit}
                    onPageChange={(e, page) => setPage(page+1)}
                    onRowsPerPageChange={e => setLimit(e.target.value)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    </Container>
  </Box>
};

export default SolutionsList;
